// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Servisni sloj
import {
  deletePricingDraftAPI,
  getPricingPacksAPI,
  getPricingVersionAPI,
  getPricingVersionsAPI,
} from "services/api/pricing/api";
import { PricingPack, PricingVersion, PricingVersionRow } from "services/api/pricing/types";

// Helpers
import { transformDateToLocalDateString, transformRoute } from "helpers";

// UI komponente
import { Badge } from "ui/components/Badge";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { PRICING_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import PricingEditCard from "ui/modules/pricing/components/PricingEditCard/PricingEditCard";
import PricingTabMenu from "ui/modules/pricing/components/PricingTabMenu/PricingTabMenu";

// Lokalne komponente
import MeteredItemsCard from "./components/MeteredItemsCard/MeteredItemsCard";
import OtherValuesCard from "./components/OtherValuesCard/OtherValuesCard";
import PriceTiersCard from "./components/PriceTiersCard/PriceTiersCard";
import VehiclePriceMatrixCard from "./components/VehiclePriceMatrixCard/VehiclePriceMatrixCard";

// Tipovi i konstante
import { PRICING_DETAIL_BREADCRUMB } from "./consts";
import { PricingDetailPageCmp, PricingDetailPageProps } from "./types";

/**
 * Jedna verzija cjenika. Do nje se dolazi iz pretrage.
 *
 * Spremanje ne mijenja zamrznutu verziju nego stvara novu s kasnijim datumom, pa nakon
 * spremanja stranica odlazi na tu novu verziju.
 */
const PricingDetailPage: PricingDetailPageCmp = (_props: PricingDetailPageProps) => {
  const title = PRICING_PAGE_TITLE;

  const { versionId } = useParams();
  const navigate = useNavigate();

  const [version, setVersion] = useState<PricingVersion | null>(null);
  const [versionRow, setVersionRow] = useState<PricingVersionRow | null>(null);
  const [packs, setPacks] = useState<PricingPack[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  useEffect(() => {
    if (versionId) fetchVersion(versionId);
  }, [versionId]);

  /**
   * Uz samu verziju se dohvaća i njezin redak iz popisa — samo on nosi `isEffective`, a o
   * njemu ovisi smije li se verzija brisati i kako se zove gumb.
   */
  const fetchVersion = async (id: string) => {
    setIsLoading(true);

    try {
      const [detail, rows, allPacks] = await Promise.all([
        getPricingVersionAPI(id),
        getPricingVersionsAPI(),
        getPricingPacksAPI(),
      ]);

      setVersion(detail);
      setVersionRow(rows.find((row) => row.id === id) ?? null);
      setPacks(allPacks);
    } catch {
      // greška se prikazuje kroz snackbar
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  /** Spremanje zamrznute verzije stvara novu, pa se ide na nju, a ne natrag na staru. */
  const handleEditSuccess = useCallback(
    async (saved: PricingVersion) => {
      setShowEditModal(false);

      navigate(
        transformRoute(Routes.PricingDetailPageRouter, { versionId: saved.id })
      );
    },
    [navigate]
  );

  const handleDeleteClick = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  /** Briše se samo nacrt — verziju koja je stupila na snagu backend odbija obrisati. */
  const handleDeleteConfirm = async () => {
    if (!versionId) return;

    setShowDeleteDialog(false);
    setIsLoading(true);

    try {
      await deletePricingDraftAPI(versionId);
    } catch {
      // greška se prikazuje kroz snackbar
      setIsLoading(false);
      return;
    }

    // Obrisana verzija nema što prikazati, pa se ide natrag na popis.
    navigate(Routes.PricingPageRouter);
  };

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              {/* Datum i status verzije stoje uz naslov — to je kontekst cijele stranice. */}
              <div className="flex flex-wrap items-center gap-3">
                <PageTitle title={title.text} icon={title.icon} iconColor={title.iconColor} />

                {version && (
                  <span className="text-2xl font-bold ">
                    - vrijedi od{" "}
                    {transformDateToLocalDateString(new Date(version.validFrom))}
                  </span>
                )}

                {/* Status je isti kao u popisu: aktivna je ona po kojoj se danas obračunava. */}
                {versionRow && (
                  <Badge
                    text={versionRow.isEffective ? "Aktivan" : "Neaktivan"}
                    variant={versionRow.isEffective ? "success" : "secondary"}
                    height="small"
                    showDot
                  />
                )}

                {/* Nacrt nije status nego stanje: samo se on smije mijenjati i brisati. */}
                {versionRow?.isDraft && (
                  <Badge text="Nacrt" variant="warning" height="small" showDot />
                )}
              </div>

              <Breadcrumb items={PRICING_DETAIL_BREADCRUMB} />
            </div>

            {version && (
              <div className="flex items-center gap-2">
                {/* Nacrt još nije stupio na snagu pa se smije obrisati; ostale ne. */}
                {versionRow?.isDraft && (
                  <Button variant="remove" children="Obriši nacrt" onClick={handleDeleteClick} />
                )}

                {/*
                  „Promijeni", ne „uredi": nacrt i verzija koja vrijedi danas mijenjaju se u
                  mjestu, a iz starije spremanje stvara novu. Prvi korak čarobnjaka kaže što
                  je od toga na redu.
                */}
                <Button
                  variant="primary"
                  children="Promijeni cjenik"
                  startIcon="/media/icons/plus.svg"
                  onClick={handleEditClick}
                />
              </div>
            )}
          </div>
        </PageHeader>
        <PricingTabMenu />
      </div>

      {/* Jedna ploha, sekcije razdvojene crtom — ne niz zasebnih bijelih kartica. */}
      <div className="">
        <div className="divide-y divide-slate-200">
          {version && (
            <>
              <VehiclePriceMatrixCard combinations={version.combinations} packs={packs} />
              <PriceTiersCard tiers={version.priceTiers} />
              <MeteredItemsCard items={version.meteredItems} />
              <OtherValuesCard values={version.otherValues} />
            </>
          )}
        </div>
      </div>

      {version && (
        <PricingEditCard
          show={showEditModal}
          version={version}
          isEffective={!!versionRow?.isEffective}
          packs={packs}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      <Dialog
        open={showDeleteDialog}
        title="Brisanje nacrta cjenika"
        message="Jeste li sigurni da želite obrisati ovaj nacrt cjenika?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      <Loader isLoading={isLoading} message="Učitavanje podataka…">
        {null}
      </Loader>
    </div>
  );
};

PricingDetailPage.route = Routes.PricingDetailPageRouter;

export default PricingDetailPage;
